import { useCallback, useMemo } from "react";

import {
  DatePicker,
  FieldSelect,
  FieldSwitch,
  Form,
  FormikForm,
  FormRowField,
  MainPanel,
} from "@skilitics/designer-ui-components";
import { useFormatMessage } from "@skilitics/localization";

import { useUsageAnalyticsDataContext } from "./UsageAnalyticsDataContext";
import { useUsageAnalyticsModuleVersions } from "./useUsageAnalyticsModuleVersions";

type FormValues = {
  dateRangeFilterEnabled: boolean;
  moduleVersionFilterEnabled: boolean;
  selectedModuleVersion?: string;
};

export const UsageAnalyticsDataPanel = () => {
  const formatMessage = useFormatMessage(formatStrings);
  const {
    dateRangeFilterEnabled,
    setDateRangeFilterEnabled,
    dateRange,
    setDateRange,
    moduleVersionFilterEnabled,
    setModuleVersionFilterEnabled,
    moduleVersion,
    setModuleVersion,
  } = useUsageAnalyticsDataContext();

  const { moduleVersions, status: statusToGetModuleVersionsData } = useUsageAnalyticsModuleVersions();

  const initialValues = useMemo(
    () => ({
      dateRangeFilterEnabled,
      moduleVersionFilterEnabled,
      selectedModuleVersion: moduleVersion && moduleVersions.includes(moduleVersion) ? moduleVersion : undefined,
    }),
    [dateRangeFilterEnabled, moduleVersionFilterEnabled, moduleVersion, moduleVersions],
  );

  const handleSubmit = useCallback(
    (values: FormValues) => {
      setDateRangeFilterEnabled(values.dateRangeFilterEnabled);
      setModuleVersionFilterEnabled(values.moduleVersionFilterEnabled);
      setModuleVersion(values.moduleVersionFilterEnabled ? values.selectedModuleVersion : undefined);
    },
    [setDateRangeFilterEnabled, setModuleVersionFilterEnabled, setModuleVersion],
  );

  return (
    <MainPanel>
      <FormikForm<FormValues>
        initialValues={initialValues}
        onSubmit={handleSubmit}
        formatStrings={formatStrings}
        submitOnBlur
        dataTest="thrive-data-form"
      >
        <Form style={{ padding: "8px 16px 0 16px" }}>
          <FormRowField<FormValues> name="dateRangeFilterEnabled">
            <FieldSwitch />
            <DatePicker
              label={formatStrings.fromDate}
              value={dateRange.startDate}
              onChange={(value) => setDateRange({ startDate: value })}
              disabled={!dateRangeFilterEnabled}
            />
          </FormRowField>

          <FormRowField<FormValues> name="moduleVersionFilterEnabled">
            <FieldSwitch />
            <FormRowField<FormValues> name="selectedModuleVersion">
              <FieldSelect
                disabled={
                  statusToGetModuleVersionsData === "pending" ||
                  statusToGetModuleVersionsData === "error" ||
                  !moduleVersionFilterEnabled ||
                  moduleVersions.length === 0
                }
                options={moduleVersions.map((value) => ({
                  label: value,
                  value,
                }))}
                label={
                  statusToGetModuleVersionsData === "pending" && moduleVersionFilterEnabled
                    ? formatMessage("loading")
                    : moduleVersions.length === 0 && moduleVersionFilterEnabled
                      ? formatMessage("nodata")
                      : undefined
                }
              />
            </FormRowField>
          </FormRowField>
        </Form>
      </FormikForm>
    </MainPanel>
  );
};

const formatStrings = {
  dateRangeFilterEnabledLabel: {
    en: "Enable Date Range Filter",
  },
  dateRangeFilterEnabledDescription: {
    en: "When enabled, analysis data will be narrowed to the specified date range.",
  },
  moduleVersionFilterEnabledLabel: {
    en: "Enable Module Version Filter",
  },
  moduleVersionFilterEnabledDescription: {
    en: "When enabled, analysis data will be narrowed to the specified module version.",
  },
  selectedModuleVersionLabel: {
    en: "Module Version",
  },
  selectedModuleVersionDescription: {
    en: "Available module versions to filter.",
  },
  loading: {
    en: "Loading ...",
  },
  nodata: {
    en: "No data found",
  },
  fromDate: {
    en: "From",
  },
};
